<template>
  <div class="th-list-subcategory">
    <h2 class="th-title"><b>Data Aset Sub Kategori</b></h2>
    <a-card>
      <div class="mb-3 row">
        <div class="col-sm-4 col-md-3 m-1">
          <a-input-search placeholder="Cari Subkategori..." v-model:value="search" allow-clear />
        </div>
        <div class="col m-1 w-100 d-flex justify-content-end align-items-center">
          <a-button type="primary" @click="openAddSubcategoryModal">
            <PlusOutlined />Tambah Subkategori
          </a-button>
        </div>
      </div>
      <div class="w-100" style="overflow-x:auto;">
        <a-table
          :pagination="globalPagination"
          :columns="columns"
          :data-source="filteredSubcategories"
          class="table"
          bordered
          table-layout="auto"
          :loading="loadingTable"
          @change="handleTableChange"
        >
          <template #name="{ text, record }">
            <div>
              <a-input
                v-if="editingKey === record.id"
                v-model:value="editableData[record.id].name"
                style="margin: -5px 0"
                @pressEnter="saveSubcategory(record.id)"
              />
              <template v-else>
                {{ text }}
              </template>
            </div>
          </template>

          <template #assetCategoryName="{ record }">
            {{ record.AssetCategory ? record.AssetCategory.name : '-' }}
          </template>

          <template #action="{ record }">
            <div class="editable-row-operations">
              <span v-if="editingKey === record.id">
                <button
                  class="btn btn-sm btn-outline-success mr-2"
                  @click="saveSubcategory(record.id)"
                >
                  <CheckOutlined /> Simpan
                </button>
                <a-popconfirm
                  title="Yakin ingin membatalkan?"
                  @confirm="cancelEditSubcategory(record.id)"
                >
                  <button class="btn btn-sm btn-outline-secondary"><CloseOutlined /> Batal</button>
                </a-popconfirm>
              </span>
              <span v-else>
                <button
                  class="btn btn-sm btn-outline-primary mr-2"
                  :disabled="editingKey !== ''"
                  @click="edit(record)"
                >
                  <EditOutlined /> Edit
                </button>
                <button
                  class="btn btn-sm btn-outline-danger"
                  :disabled="editingKey !== ''"
                  @click="openDeleteModal(record)"
                >
                  <DeleteOutlined /> Hapus
                </button>
              </span>
            </div>
          </template>
        </a-table>
      </div>
    </a-card>

    <a-modal
      v-model:visible="addSubcategoryModalVisible"
      title="Tambah Subkategori Aset Baru"
      @ok="handleAddSubcategory"
      @cancel="cancelAddSubcategory"
      :confirm-loading="addSubcategoryLoading"
      ok-text="Tambah"
      cancel-text="Batal"
      centered
    >
      <a-form layout="vertical">
        <a-form-item label="Nama Subkategori" required>
          <a-input v-model:value="newSubcategoryName" placeholder="Masukkan nama subkategori" />
        </a-form-item>
        <a-form-item label="Kategori Aset Induk" required>
          <a-select v-model:value="selectedAssetCategoryId" placeholder="Pilih kategori aset">
            <a-select-option
              v-for="category in assetCategories"
              :key="category.id"
              :value="category.id"
            >
              {{ category.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
      </a-form>
    </a-modal>

    <a-modal
      v-model:visible="deleteModalVisible"
      title="Konfirmasi Penghapusan"
      @ok="confirmDeleteSubcategory"
      @cancel="cancelDeleteSubcategory"
      :confirm-loading="deleteLoading"
      ok-text="Hapus"
      cancel-text="Batal"
      ok-type="danger"
      centered
    >
      <p>
        <ExclamationCircleOutlined class="text-warning me-2" />
        Anda yakin ingin menghapus subkategori
        <b>"{{ subcategoryToDelete ? subcategoryToDelete.name : '' }}"</b> ini secara permanen?
      </p>
      <p>Tindakan ini tidak dapat dibatalkan.</p>
    </a-modal>
  </div>
</template>

<script>
import { onMounted, ref, computed, reactive } from 'vue'
import {
  PlusOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons-vue'
import {
  getAssetSubCategory,
  createAssetSubCategory,
  updateAssetSubCategory,
  deleteAssetSubCategory,
  getAssetTypeCategory,
} from '../../../services/axios/modc/assets'
import lodash from 'lodash'
import { notification } from 'ant-design-vue'

export default {
  components: {
    PlusOutlined,
    ExclamationCircleOutlined,
    EditOutlined,
    DeleteOutlined,
    CheckOutlined,
    CloseOutlined,
  },
  setup() {
    const subcategories = ref([])
    const assetCategories = ref([])
    const search = ref('')
    const loadingTable = ref(false)
    const globalPagination = ref({ current: 1, pageSize: 10 })

    const editableData = reactive({})
    const editingKey = ref('') // Kunci untuk menandai baris yang sedang diedit

    const deleteModalVisible = ref(false)
    const subcategoryToDelete = ref(null)
    const deleteLoading = ref(false)

    const addSubcategoryModalVisible = ref(false)
    const newSubcategoryName = ref('')
    const selectedAssetCategoryId = ref(undefined)
    const addSubcategoryLoading = ref(false)

    const columns = ref([
      {
        title: 'Nama Subkategori',
        dataIndex: 'name',
        key: 'name',
        slots: { customRender: 'name' },
      },
      {
        title: 'Kategori Aset Induk',
        key: 'assetCategoryName',
        slots: { customRender: 'assetCategoryName' },
      },
      {
        title: 'Aksi',
        key: 'action',
        slots: { customRender: 'action' },
        width: 220,
        align: 'center',
      },
    ])

    // --- Logika Fetch Data ---
    const fetchSubcategories = async () => {
      loadingTable.value = true
      try {
        const { categories: fetchedSubcategories } = await getAssetSubCategory()
        subcategories.value = fetchedSubcategories.map(subcat => ({ ...subcat, key: subcat.id }))
        globalPagination.value.total = fetchedSubcategories.length
      } catch (error) {
        notification.error({ message: 'Gagal', description: 'Gagal memuat subkategori aset!' })
      } finally {
        loadingTable.value = false
      }
    }

    const fetchAssetCategories = async () => {
      try {
        const { categories } = await getAssetTypeCategory()
        assetCategories.value = categories
      } catch (error) {
        notification.error({ message: 'Gagal', description: 'Gagal memuat daftar kategori aset!' })
      }
    }

    onMounted(() => {
      fetchSubcategories()
      fetchAssetCategories()
    })

    // --- Logika Edit ---
    const edit = record => {
      editableData[record.id] = lodash.cloneDeep(record)
      editingKey.value = record.id
    }

    const saveSubcategory = async id => {
      const target = subcategories.value.find(item => id === item.id)
      if (target && editableData[id]) {
        if (!editableData[id].name.trim()) {
          notification.warning({
            message: 'Peringatan',
            description: 'Nama subkategori tidak boleh kosong!',
          })
          return
        }
        try {
          await updateAssetSubCategory({
            id: editableData[id].id,
            name: editableData[id].name,
          })
          notification.success({
            message: 'Berhasil',
            description: `Subkategori "${editableData[id].name}" berhasil diperbarui!`,
          })
          Object.assign(target, editableData[id])
          delete editableData[id]
          editingKey.value = '' // Reset kunci edit
        } catch (error) {
          notification.error({
            message: 'Gagal',
            description: `Gagal memperbarui subkategori. ${error.message || ''}`,
          })
        }
      }
    }

    const cancelEditSubcategory = id => {
      delete editableData[id]
      editingKey.value = '' // Reset kunci edit
    }

    // --- Logika Delete ---
    const openDeleteModal = record => {
      subcategoryToDelete.value = record
      deleteModalVisible.value = true
    }

    const confirmDeleteSubcategory = async () => {
      if (!subcategoryToDelete.value) return
      deleteLoading.value = true
      try {
        await deleteAssetSubCategory(subcategoryToDelete.value.id)
        notification.success({
          message: 'Berhasil',
          description: `Subkategori "${subcategoryToDelete.value.name}" berhasil dihapus!`,
        })
        await fetchSubcategories()
        deleteModalVisible.value = false
      } catch (error) {
        notification.error({
          message: 'Gagal',
          description: `Gagal menghapus subkategori. ${error.message || ''}`,
        })
      } finally {
        deleteLoading.value = false
        subcategoryToDelete.value = null
      }
    }

    const cancelDeleteSubcategory = () => {
      deleteModalVisible.value = false
      subcategoryToDelete.value = null
    }

    // --- Logika Add ---
    const openAddSubcategoryModal = () => {
      addSubcategoryModalVisible.value = true
      newSubcategoryName.value = ''
      selectedAssetCategoryId.value = undefined
    }

    const handleAddSubcategory = async () => {
      if (!newSubcategoryName.value.trim() || !selectedAssetCategoryId.value) {
        notification.warning({ message: 'Peringatan', description: 'Semua kolom wajib diisi!' })
        return
      }
      addSubcategoryLoading.value = true
      try {
        const payload = {
          name: newSubcategoryName.value,
          assetCategoryId: selectedAssetCategoryId.value,
        }
        await createAssetSubCategory(payload)
        notification.success({
          message: 'Berhasil',
          description: `Subkategori "${newSubcategoryName.value}" berhasil ditambahkan!`,
        })
        addSubcategoryModalVisible.value = false
        await fetchSubcategories()
      } catch (error) {
        notification.error({
          message: 'Gagal',
          description: `Gagal menambahkan subkategori. ${error.message || ''}`,
        })
      } finally {
        addSubcategoryLoading.value = false
      }
    }

    const cancelAddSubcategory = () => {
      addSubcategoryModalVisible.value = false
    }

    // --- Logika Lainnya ---
    const filteredSubcategories = computed(() => {
      if (!search.value) return subcategories.value
      const lowerCaseSearch = search.value.toLowerCase()
      return subcategories.value.filter(
        subcat =>
          (subcat.name && subcat.name.toLowerCase().includes(lowerCaseSearch)) ||
          (subcat.AssetCategory?.name &&
            subcat.AssetCategory.name.toLowerCase().includes(lowerCaseSearch)),
      )
    })

    const handleTableChange = pag => {
      globalPagination.value.current = pag.current
    }

    return {
      search,
      globalPagination,
      columns,
      loadingTable,
      handleTableChange,
      filteredSubcategories,
      editingKey,
      editableData,
      edit,
      saveSubcategory,
      cancelEditSubcategory,
      deleteModalVisible,
      subcategoryToDelete,
      deleteLoading,
      openDeleteModal,
      confirmDeleteSubcategory,
      cancelDeleteSubcategory,
      addSubcategoryModalVisible,
      newSubcategoryName,
      selectedAssetCategoryId,
      assetCategories,
      addSubcategoryLoading,
      openAddSubcategoryModal,
      handleAddSubcategory,
      cancelAddSubcategory,
    }
  },
}
</script>

<style lang="scss">
@import '@/views/styleViews.scss';

.editable-row-operations a {
  margin-right: 8px;
}

.text-warning {
  color: #faad14;
}
</style>
